import {useLingui} from '@lingui/react/macro';
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {useForm, Controller} from 'react-hook-form';
import {useToast} from 'react-native-toast-notifications';
import {ScrollView, StatusBar, View, Text, StyleSheet, Pressable} from 'react-native';
import {Svg, Path} from 'react-native-svg';
import {TextInput} from 'interface/base/Input';
import {IconButton} from 'interface/base/IconButton';
import {useNavigate} from 'extensions/navigation';
import device from 'extensions/device';
import {getHost} from 'utils/platform';
import {createSession, login, load, loadTrash, openPage} from 'utils/mediafire';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';
export interface LoginForm {
  email: string;
  password: string;
}

export function Login() {
  const {t} = useLingui();
  const host = getHost();
  const toast = useToast();
  const form = useForm<LoginForm>();
  const isDark = device.isDarkMode();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const submit = useCallback(async (data: LoginForm) => {
    device.dismissKeyboard();
    try {
      await createSession(data);
      await login(dispatch);
      load(navigate, dispatch, 'myfiles', true);
      loadTrash(navigate, dispatch, true);
    } catch (error) {
      toast.show(error.message, {type: 'danger'});
    }
  }, []);

  return (
    <ScrollView
      bounces={false}
      style={styles.root}
      keyboardShouldPersistTaps="always"
      contentContainerStyle={styles.content}>
      <StatusBar
        barStyle={isDark ? 'light-content' : 'dark-content'}
        backgroundColor={theme.login.fill}
      />
      <View style={styles.form}>
        <View style={styles.logo}>
          <Svg viewBox="0 0 291 41" width={224} height={32}>
            <Path d={vectors.logo.flame} fill={isDark ? colors.neutral.white : colors.primary.brand}/>
            {vectors.logo.letters.map((d,i) =>
              <Path key={i} d={d} fill={isDark ? colors.neutral.white : colors.primary.dark}/>
            )}
          </Svg>
        </View>
        <Text style={styles.header}>
          {t`Log in to your account`}
        </Text>
        <View style={styles.field}>
          <Controller
            name="email"
            defaultValue=""
            control={form.control}
            rules={{required: true, pattern: /.+@.+\../}}
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                autoFocus
                blurOnSubmit={false}
                enablesReturnKeyAutomatically
                autoCapitalize="none"
                returnKeyType="next"
                importantForAutofill="yes"
                textContentType="username"
                // @ts-ignore
                autoCompleteType="username"
                keyboardType="email-address"
                placeholder={t`Email address`}
                onSubmitEditing={form.handleSubmit(submit)}
                onChangeText={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          {form.formState.errors.email?.type === 'required' &&
            <Text style={styles.error}>
              {t`Missing email address`}
            </Text>
          }
          {form.formState.errors.email?.type === 'pattern' &&
            <Text style={styles.error}>
              {t`Invalid email address`}
            </Text>
          } 
        </View>
        <View style={styles.field}>
          <Controller
            name="password"
            defaultValue=""
            control={form.control}
            rules={{required: true, minLength: 3, maxLength: 30}}
            render={({field: {onChange, onBlur, ref, value}}) => (
              <TextInput
                inputRef={ref}
                secureTextEntry
                enablesReturnKeyAutomatically
                returnKeyType="send"
                importantForAutofill="yes"
                textContentType="password"
                // @ts-ignore
                autoCompleteType="password"
                placeholder={t`Password`}
                onSubmitEditing={form.handleSubmit(submit)}
                onChangeText={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          {form.formState.errors.password?.type === 'required' &&
            <Text style={styles.error}>
              {t`Missing password`}
            </Text>
          }
          {(form.formState.errors.password?.type === 'minLength'
            || form.formState.errors.password?.type === 'maxLength') &&
            <Text style={styles.error}>
              {t`Invalid password`}
            </Text>
          }
        </View>
        <View style={styles.actions}>
          <Pressable onPress={() => openPage(`${host}/lost_password.php`)}>
            <Text style={styles.passwordReset}>{t`Forgot Password?`}</Text>
          </Pressable>
        </View>
        <IconButton
          mode="primary"
          text={t`Log in`.toUpperCase()}
          textStyle={styles.loginText}
          buttonStyle={styles.loginButton}
          onPress={form.handleSubmit(submit)}
        />
        <View style={styles.signupBox}>
          <Text style={styles.signupHint}>
            {t`Don't have an account?`}
          </Text>
          <Pressable onPress={() => openPage(`${host}/upgrade/registration.php?pid=66`)}>
            <Text style={styles.signupLink}>
              {t`Create an account`}
            </Text>
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
}

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.login.fill,
  },
  content: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    marginHorizontal: 30,
    marginVertical: 60,
    maxWidth: 400,
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    marginTop: 5,
    marginBottom: 25,
    alignItems: 'center',
  },
  header: {
    marginBottom: 10,
    fontSize: 20,
    fontWeight: '300',
    textAlign: 'center',
    color: theme.login.header,
  },
  error: {
    fontSize: 12,
    marginTop: 8,
    color: colors.secondary.brightRed,
  },
  field: {
    marginTop: 20,
    borderWidth: 1,
    borderRadius: 3,
    borderColor: theme.login.signup,
  },
  loginButton: {
    height: 54,
    minHeight: 54,
  },
  loginText: {
    fontSize: 14,
    color: colors.neutral.dark,
  },
  actions: {
    marginBottom: 20,
    alignItems: 'flex-end',
  },
  passwordReset: {
    fontSize: 16,
    paddingVertical: 8,
    color: colors.primary.brand,
  },
  signupBox: {
    padding: 20,
    marginTop: 30,
    borderRadius: 3,
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: theme.login.signup,
  },
  signupHint: {
    fontSize: 16,
    marginRight: 10,
    color: theme.login.signupText,
  },
  signupLink: {
    fontSize: 16,
    color: colors.primary.brand,
  },
});
